<template>
  <!-- Body -->
  <div>
    <div class="sprite-container" v-if="isOngoingEvent && showSprite">
      <div class="sprite"></div>
    </div>
    <div class="d-flex flex-column align-center">
      <div id="building">
        <div id="screen" @click="enter">
          <div class="tv-effect-container">
            <div v-if="showScanLine" class="tv-scan-line"></div>
            <v-img id="screenImage" :class="{ visible: showBackground }" :src="currentNews && currentNews.imageUrl
              ? currentNews.imageUrl
              : defaultNews.imageUrl
              " height="261" width="261" contain lazy></v-img>
          </div>
          <v-img id="screenFrame" :src="require('@/assets/world/buildings/WorldBuildingNewsScreenFrame.png')
            " lazy></v-img>
          <div id="screenCaption">
            <div id="captionBackground">
              <div ref="scrollingText" class="scrolling-text">
                {{
                  currentNews && currentNews.caption ? currentNews.caption : ""
                }}
              </div>
            </div>
            <v-img id="captionFrame" :src="require('@/assets/world/buildings/WorldBuildingNewsCaptionFrame.png')
              " lazy></v-img>
          </div>

        </div>
        <div class="hamochi-container">
          <WorldHamochi />
        </div>
      </div>
      <newsDialog ref="ref_news" @newsMounted="onNewsMounted" />
      <eventDialog ref="ref_event" @eventMounted="onEventMounted" />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  components: {
    newsDialog: () =>
      import(
        /* webpackChunkName: "component-news-dialog" */ "@/components/news/newsDialog.vue"
      ),
    eventDialog: () =>
      import(
        /* webpackChunkName: "component-event-dialog" */ "@/components/news/eventDialog.vue"
      ),
    WorldHamochi: () =>
      import(
        /* webpackChunkName: "component-world-hamochi" */ "@/components/world/WorldHamochi"
      ),
  },
  computed: mapState({
    auth: (state) => state.auth.data,
    settings: (state) => state.settings.data,
  }),
  props: {
    isOngoingEvent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showBackground: false,
    showScanLine: false,
    showSprite: false,
    newsList: [],
    currentNewsIndex: 0,
    newsRotationInterval: null,
    defaultNews: {
      caption: "",
      imageUrl: require("@/assets/world/buildings/News1.png"),
    },
    currentNews: null,
    api: {
      isLoading: false,
      isError: false,
      error: null,
      url: null,
    },
  }),
  created() {
    // Set default news caption based on locale
    const defaultTexts = {
      en: "Welcome to Mochi Island, where we learn and play!",
      ms: "Selamat datang ke Pulau Mochi, tempat kita belajar dan bermain!",
      zh: "欢迎来到麻糬岛，在这里我们一起学习和玩耍！",
    };
    this.defaultNews.caption = defaultTexts[this.$_getLocale()];

    // Set up API configuration
    this.api.url =
      this.$api.servers.game +
      "/api/v1/" +
      this.$_getLocale() +
      "/game/news/visit";
    this.api.callbackReset = () => {
      this.api.isLoading = true;
      this.api.isError = false;
      this.api.error = null;
    };
    this.api.callbackError = (e) => {
      this.api.isLoading = false;
      this.api.isError = true;
      this.api.error = e;
      console.error("Error fetching news:", e);

      // Show default news with TV effect if API fails
      this.currentNews = this.defaultNews;
      this.startTvEffect();
    };
    this.api.callbackSuccess = (resp) => {
      this.api.isLoading = false;

      if (resp.News && resp.News.length > 0) {
        resp.News.forEach((news) => {
          news.caption = `${news.title}: ${news.body}`;
        });
        this.newsList = resp.News;

        this.currentNews = this.newsList[0];

        // Start news rotation if we have multiple news items
        if (this.newsList.length > 1) {
          this.startNewsRotation();
        }
      } else {
        // Use default news if no news items returned
        this.currentNews = this.defaultNews;
      }

      // Start TV effect after API is loaded successfully
      this.startTvEffect();
    };

    this.$api.fetch(this.api);
  },
  mounted() {
    setTimeout(() => {
      this.showSprite = true;
    }, 1000);
  },
  beforeDestroy() {
    // Clear interval when component is destroyed
    if (this.newsRotationInterval) {
      clearInterval(this.newsRotationInterval);
    }
    this.showSprite = false;
  },
  methods: {
    onNewsMounted() {
      this.$refs["ref_news"].fetch();
    },
    onEventMounted() {
      this.$refs["ref_event"].fetch();
    },
    startTvEffect() {
      // Update scrolling text animation
      this.$nextTick(() => {
        if (this.$refs.scrollingText) {
          this.$refs.scrollingText.classList.add("animate");
        }
      });

      // Show scan line effect
      this.showScanLine = true;

      // Show background after scan line effect
      setTimeout(() => {
        this.showBackground = true;

        // Hide scan line after effect completes
        setTimeout(() => {
          this.showScanLine = false;
        }, 500);
      }, 500);
    },
    enter() {
      this.$refs["ref_news"].loadNews(this.currentNewsIndex);
    },
    startNewsRotation() {
      // Clear any existing interval
      if (this.newsRotationInterval) {
        clearInterval(this.newsRotationInterval);
      }

      // Start news rotation
      this.newsRotationInterval = setInterval(() => {
        this.rotateNews();
      }, 15000); // Rotate every 15 seconds
    },
    rotateNews() {
      if (this.newsList.length <= 1) return;

      // Move to next news item
      this.currentNewsIndex =
        (this.currentNewsIndex + 1) % this.newsList.length;
      this.currentNews = this.newsList[this.currentNewsIndex];

      // Reset and restart scrolling animation
      if (this.$refs.scrollingText) {
        this.$refs.scrollingText.classList.remove("animate");

        // Force reflow
        void this.$refs.scrollingText.offsetWidth;

        // Add animation class back
        this.$refs.scrollingText.classList.add("animate");
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.showSprite = false;
    setTimeout(() => {
      next();
    }, 100);
  },
};
</script>

<style scoped>
#building {
  position: absolute;
  background-image: url("../../../assets/world/buildings/WorldBuildingNews_960x512.png");
  width: 100vw;
  height: 100vh;
  bottom: 160px;
  background-size: auto;
  background-position: center bottom;
}

#screen {
  cursor: pointer;
  position: absolute;
  width: 279px;
  height: 280px;
  bottom: 180px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  filter: drop-shadow(3px 0 0 rgba(255, 255, 255, 0.3)) drop-shadow(-3px 0 0 rgba(255, 255, 255, 0.3)) drop-shadow(0 3px 0 rgba(255, 255, 255, 0.3)) drop-shadow(0 -3px 0 rgba(255, 255, 255, 0.3));
}

/* TV effect styles */
.tv-effect-container {
  position: absolute;
  width: 261px;
  height: 261px;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: black;
  overflow: hidden;
  border: solid 5px white;
}

.tv-scan-line {
  position: absolute;
  width: 100%;
  height: 2px;
  background-color: white;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  animation: scanLine 0.5s ease-out;
  opacity: 0;
}

@keyframes scanLine {
  0% {
    height: 2px;
    opacity: 1;
  }

  50% {
    height: 40px;
    opacity: 0.8;
  }

  100% {
    height: 2px;
    opacity: 0;
  }
}

#screenImage {
  opacity: 0;
  transition: opacity 0.3s ease-in;
  background-color: white;
}

#screenImage.visible {
  opacity: 1;
}

#screenFrame {
  position: relative;
  width: 279px;
  height: 280px;
}

#screen:hover {
  /* transform: scale(1.05); */
  filter: drop-shadow(0 0 4px rgba(255, 255, 0, 0.8)) drop-shadow(0 0 8px rgba(255, 255, 0, 0.6)) drop-shadow(0 0 12px rgba(255, 255, 0, 0.4));
}

#screenCaption {
  position: absolute;
  margin: auto;
  bottom: -60px;
  left: 0;
  right: 0;
}

#captionBackground {
  position: absolute;
  background-color: black;
  width: 261px;
  height: 70px;
  margin: auto;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border: solid 5px white;
  border-radius: 10px;
  color: white;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
}

.scrolling-text {
  white-space: nowrap;
  position: absolute;
  transform: translateX(50%);
}

.scrolling-text.animate {
  animation: scrollText 15s linear infinite;
  transform: none;
}

@keyframes scrollText {
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(-100%);
  }
}

.sprite-container {
  width: 300px;
  height: 300px;
  margin: auto;
  overflow: hidden;
  position: fixed;
  left: 0;
  bottom: 150px;
  z-index: 9;
  pointer-events: none;
}

.sprite {
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: bottom left;
  animation: swapImage 1s steps(1) infinite;
}

/* Keyframes to swap between two images */
@keyframes swapImage {
  0% {
    background-image: url("../../../assets/world/shout_1.png");
  }

  50% {
    background-image: url("../../../assets/world/shout_3.png");
  }

  100% {
    background-image: url("../../../assets/world/shout_1.png");
  }
}

.hamochi-container {
  width: 100%;
  position: absolute;
  bottom: 45px;
}
</style>